import { errorMessage } from "@atlas/shared";
import React from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Map as MapIcon, MountainSnow, RotateCcw } from "lucide-react";
import {
  fetchAdminUser,
  fetchAdminUserActivities,
  fetchAdminUserShowcaseUrl,
  reprocessAdminUserActivities,
  type AdminUserActivityRow,
} from "../../api/admin";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { RerunMatchingButton } from "./AdminPipelinePage";

const PAGE_SIZE = 50;

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleDateString() : "—";
}

function formatDistance(distanceM: number | null) {
  return distanceM != null ? `${(distanceM / 1000).toFixed(1)} km` : "—";
}

function formatDuration(seconds: number | null) {
  if (seconds == null) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Mints the target user's showcase link server-side and opens it in a new tab.
// The window is opened synchronously so popup blockers don't eat it.
export function OpenShowcaseButton({ userId }: { userId: string }) {
  const mintMutation = useMutation({
    mutationFn: () => fetchAdminUserShowcaseUrl(userId),
  });

  const open = () => {
    const w = window.open("", "_blank");
    mintMutation.mutate(undefined, {
      onSuccess: ({ url }) => {
        if (w) w.location.href = url;
        else window.open(url, "_blank");
      },
      onError: () => w?.close(),
    });
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<MapIcon size={14} />}
        disabled={mintMutation.isPending}
        onClick={open}
      >
        {mintMutation.isPending ? "Opening…" : "Open exploration showcase"}
      </Button>
      {mintMutation.isError && (
        <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
          {errorMessage(mintMutation.error, "Failed to open")}
        </span>
      )}
    </div>
  );
}

// Queues every activity with a track for the user onto the process-activity
// queue (POST /admin/pipeline/users/:id/reprocess, audit-logged).
function RerunAllButton({ userId, count }: { userId: string; count: number }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => reprocessAdminUserActivities(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin/users", userId, "activities"] });
    },
  });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<RotateCcw size={14} />}
        disabled={mutation.isPending || count === 0}
        onClick={() => {
          if (window.confirm(`Re-run matching for all ${count.toLocaleString()} of this user's activities? They are queued in order and processed a few at a time.`)) {
            mutation.mutate();
          }
        }}
      >
        {mutation.isPending ? "Queuing…" : "Re-run all matching"}
      </Button>
      {mutation.isSuccess && (
        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {mutation.data.queued.toLocaleString()} queued{mutation.data.skippedNoGeom > 0 ? `, ${mutation.data.skippedNoGeom} skipped (no track)` : ""}
        </span>
      )}
      {mutation.isError && (
        <span style={{ fontSize: 12, color: "var(--status-danger)" }}>
          {errorMessage(mutation.error, "Failed")}
        </span>
      )}
    </div>
  );
}

export default function AdminUserActivitiesPage() {
  const { user: me } = useAuth();
  const { userId } = useParams({ strict: false }) as { userId: string };
  const [offset, setOffset] = React.useState(0);

  const userQuery = useQuery({
    queryKey: ["admin/users", userId],
    queryFn: () => fetchAdminUser(userId),
    enabled: !!me?.isAdmin && !!userId,
  });

  const activitiesQuery = useQuery({
    queryKey: ["admin/users", userId, "activities", offset],
    queryFn: () => fetchAdminUserActivities(userId, { limit: PAGE_SIZE, offset }),
    enabled: !!me?.isAdmin && !!userId,
  });

  if (!me?.isAdmin) return <AccessDenied />;

  const target = userQuery.data?.user;
  const rows = activitiesQuery.data?.activities ?? [];
  const total = activitiesQuery.data?.total ?? 0;

  const gridColumns = "minmax(0,2fr) 100px 90px 80px 80px 90px 150px";
  const headerCell: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <AdminShell
      title="Users"
      action={
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <RerunAllButton userId={userId} count={total} />
          <OpenShowcaseButton userId={userId} />
        </div>
      }
    >
      <div>
        <Link
          to="/admin/users"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            color: "var(--text-secondary)",
            textDecoration: "none",
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 10,
          }}
        >
          <ChevronLeft size={16} /> All users
        </Link>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>
            <Link to="/admin/users/$userId" params={{ userId }} style={{ color: "inherit", textDecoration: "none" }}>
              {target?.name ?? "Activities"}
            </Link>
          </h2>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
            {target?.email ?? ""}
          </span>
          <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)" }} className="tnum">
            {total.toLocaleString()} activit{total === 1 ? "y" : "ies"}
          </span>
        </div>
      </div>

      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-sm), var(--ring-top)",
          padding: "6px 18px 12px",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            gap: 12,
            padding: "10px 0 8px",
            borderBottom: "1px solid var(--border-subtle)",
            minWidth: 860,
          }}
        >
          <span style={headerCell}>Activity</span>
          <span style={headerCell}>Source</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Date</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Distance</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Duration</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Status</span>
          <span style={{ ...headerCell, textAlign: "right" }}>Actions</span>
        </div>

        {activitiesQuery.isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
            <Skel height={30} />
            <Skel height={30} />
            <Skel height={30} />
            <Skel height={30} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<MountainSnow size={20} />}
            title="No activities"
            hint="This user has not imported any activities yet."
            style={{ margin: "12px 0" }}
          />
        ) : (
          rows.map((a: AdminUserActivityRow) => (
            <Link
              key={a.id}
              to="/admin/users/$userId/activities/$activityId"
              params={{ userId, activityId: a.id }}
              style={{
                display: "grid",
                gridTemplateColumns: gridColumns,
                gap: 12,
                alignItems: "center",
                padding: "9px 0",
                borderBottom: "1px solid var(--border-subtle)",
                minWidth: 860,
                textDecoration: "none",
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.background = "var(--surface-hover)"; }}
              onMouseLeave={(e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.style.background = "transparent"; }}
            >
              <span style={{ fontSize: 13, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.name}
              </span>
              <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>{a.sourceType}</span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right" }}>
                {formatDate(a.startedAt)}
              </span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-primary)", fontFamily: "var(--font-mono)", textAlign: "right" }}>
                {formatDistance(a.distanceM)}
              </span>
              <span className="tnum" style={{ fontSize: 12, color: "var(--text-primary)", fontFamily: "var(--font-mono)", textAlign: "right" }}>
                {formatDuration(a.durationSeconds)}
              </span>
              <span style={{ textAlign: "right" }}>
                <Badge variant={a.processed ? "success" : "neutral"}>
                  {a.processed ? "Processed" : "Pending"}
                </Badge>
              </span>
              <span style={{ display: "flex", justifyContent: "flex-end" }}>
                <RerunMatchingButton activityId={a.id} />
              </span>
            </Link>
          ))
        )}

        {total > PAGE_SIZE && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, paddingTop: 12 }}>
            <span style={{ fontSize: 12, color: "var(--text-muted)" }} className="tnum">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString()}
            </span>
            <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Button>
            <Button variant="secondary" size="sm" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>
              Next
            </Button>
          </div>
        )}
      </div>
    </AdminShell>
  );
}
